import React, { useEffect, useState } from 'react'
import { Paper, Stack, Typography, Button, Link as MuiLink } from '@mui/material'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  shouldShowCookieConsentBanner,
  setStoredCookieConsent,
} from 'src/lib/cookieConsentRegion'

/**
 * @param {{ privacyHref: string }} props
 * Same-origin SPA paths (starting with `/`) use React Router; absolute URLs open in a new tab.
 */
export default function CookieConsentBanner({ privacyHref }) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (shouldShowCookieConsentBanner()) setVisible(true)
  }, [])

  if (!visible) return null

  const close = choice => {
    setStoredCookieConsent(choice)
    setVisible(false)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('oujat:cookie-consent', { detail: choice }))
    }
  }

  const policyLinkSx = { color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }

  return (
    <Paper
      component='aside'
      elevation={12}
      role='dialog'
      aria-label={t('cookieConsent.ariaLabel')}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 11000,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 0,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        maxHeight: '45vh',
        overflow: 'auto',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent='space-between'
      >
        <Typography variant='body2' sx={{ maxWidth: { md: '70%' } }}>
          {t('cookieConsent.messageBeforeLink')}{' '}
          {privacyHref.startsWith('/') ? (
            <Link to={privacyHref} style={{ fontWeight: 600, color: 'inherit' }}>
              {t('cookieConsent.privacyPolicy')}
            </Link>
          ) : (
            <MuiLink href={privacyHref} target='_blank' rel='noopener noreferrer' sx={policyLinkSx}>
              {t('cookieConsent.privacyPolicy')}
            </MuiLink>
          )}{' '}
          {t('cookieConsent.messageAfterLink')}
        </Typography>
        <Stack direction='row' spacing={1} flexWrap='wrap' justifyContent={{ xs: 'flex-end', md: 'flex-end' }}>
          <Button variant='outlined' color='inherit' size='small' onClick={() => close('essential')}>
            {t('cookieConsent.essentialOnly')}
          </Button>
          <Button variant='contained' color='primary' size='small' onClick={() => close('all')}>
            {t('cookieConsent.accept')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
